import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_URL } from '../components/constants';

// Async thunk for fetching all organizations
export const fetchAllOrganizations = createAsyncThunk(
  'organizations/fetchAllOrganizations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/organizations`);
      return response.data.organizations;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch organizations');
    }
  }
);

// Async thunk for creating a new organization
export const createOrganization = createAsyncThunk(
  'organizations/createOrganization',
  async (organizationData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/organizations`, organizationData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create organization');
    }
  }
);

// Async thunk for updating an organization
export const updateOrganization = createAsyncThunk(
  'organizations/updateOrganization',
  async (organizationData, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_URL}/organizations`, organizationData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update organization');
    }
  }
);

// Async thunk for deleting an organization
export const deleteOrganization = createAsyncThunk(
  'organizations/deleteOrganization',
  async (organizationId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${BASE_URL}/organizations/${organizationId}`);
      return { organizationId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete organization');
    }
  }
);

// Async thunk for updating user organization
export const updateUserOrganization = createAsyncThunk(
  'organizations/updateUserOrganization',
  async (userOrgData, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_URL}/users/organization`, userOrgData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update user organization');
    }
  }
);

// Async thunk for assigning subscription to organization
export const assignSubscriptionToOrganization = createAsyncThunk(
  'organizations/assignSubscription',
  async ({ organization_id, subscription_id }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_URL}/organizations/subscription`, {
        organization_id,
        subscription_id
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to assign subscription');
    }
  }
);

const organizationsSlice = createSlice({
  name: 'organizations',
  initialState: {
    organizations: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    loading: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearStatus: (state) => {
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all organizations
      .addCase(fetchAllOrganizations.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(fetchAllOrganizations.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.organizations = action.payload;
        state.loading = false;
      })
      .addCase(fetchAllOrganizations.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.loading = false;
      })
      // Create organization
      .addCase(createOrganization.pending, (state) => {
        state.loading = true;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.loading = false;
        // Refresh the organizations list
        state.status = 'idle';
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // Update organization
      .addCase(updateOrganization.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.loading = false;
        // Refresh the organizations list
        state.status = 'idle';
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // Delete organization
      .addCase(deleteOrganization.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteOrganization.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the deleted organization from the list
        state.organizations = state.organizations.filter(
          org => org.organization_id !== action.payload.organizationId
        );
      })
      .addCase(deleteOrganization.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // Update user organization
      .addCase(updateUserOrganization.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserOrganization.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateUserOrganization.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // Assign subscription to organization
      .addCase(assignSubscriptionToOrganization.pending, (state) => {
        state.loading = true;
      })
      .addCase(assignSubscriptionToOrganization.fulfilled, (state, action) => {
        state.loading = false;
        // Refresh the organizations list
        state.status = 'idle';
      })
      .addCase(assignSubscriptionToOrganization.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
  },
});

export const { clearError, clearStatus } = organizationsSlice.actions;
export default organizationsSlice.reducer; 